import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import SubdirectoryArrowRightOutlinedIcon from "@mui/icons-material/SubdirectoryArrowRightOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  Fade,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { ImobiliarioEdificacao } from "../../interfaces";

export interface OpcaoVinculoUi {
  value: string;
  titulo: string;
  descricao: string;
  icon: React.ReactNode;
  accent: string;
}

export function IconComplementarSubunidade() {
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        width: 20,
        height: 20,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LinkOutlinedIcon sx={{ fontSize: 18 }} />
      <SubdirectoryArrowRightOutlinedIcon
        sx={{
          fontSize: 13,
          position: "absolute",
          bottom: -1,
          right: -5,
        }}
      />
    </Box>
  );
}

export function OpcaoCard({
  opcao,
  selecionada,
  onSelect,
}: {
  opcao: OpcaoVinculoUi;
  selecionada: boolean;
  onSelect: () => void;
}) {
  const theme = useTheme();

  return (
    <Box
      role="radio"
      aria-checked={selecionada}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        p: 1.75,
        borderRadius: 2,
        border: "1px solid",
        borderColor: selecionada ? alpha(opcao.accent, 0.55) : "divider",
        bgcolor: selecionada ? alpha(opcao.accent, 0.05) : "transparent",
        cursor: "pointer",
        transition: "border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease",
        "&:hover": {
          borderColor: selecionada
            ? alpha(opcao.accent, 0.7)
            : alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.18 : 0.12),
          bgcolor: selecionada
            ? alpha(opcao.accent, 0.08)
            : theme.palette.action.hover,
        },
        "&:hover .opcao-card-icon": {
          bgcolor: alpha(opcao.accent, selecionada ? 0.15 : 0.1),
        },
        "&:focus-visible": {
          outline: "none",
          boxShadow: `0 0 0 2px ${alpha(opcao.accent, 0.35)}`,
        },
      }}
    >
      <Box
        className="opcao-card-icon"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 38,
          height: 38,
          borderRadius: 1.5,
          flexShrink: 0,
          bgcolor: alpha(opcao.accent, selecionada ? 0.12 : 0.07),
          color: opcao.accent,
          transition: "background-color 0.18s ease",
        }}
      >
        {opcao.icon}
      </Box>
      <Box sx={{ minWidth: 0, pt: 0.35 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          color="text.primary"
          sx={{ fontSize: "0.9rem", lineHeight: 1.4 }}
        >
          {opcao.titulo}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ display: "block", mt: 0.35, fontSize: "0.8125rem", lineHeight: 1.45 }}
        >
          {opcao.descricao}
        </Typography>
      </Box>
    </Box>
  );
}

export function SubunidadeReferenciaSelect({
  subunidades,
  subunidadeAlvoId,
  onChange,
  labelId = "subunidade-referencia-label",
  getOptionValue = (sub) => String(sub.id),
}: {
  subunidades: ImobiliarioEdificacao[];
  subunidadeAlvoId: string;
  onChange: (id: string) => void;
  labelId?: string;
  getOptionValue?: (sub: ImobiliarioEdificacao) => string;
}) {
  return (
    <Box
      sx={{
        mt: 0.75,
        ml: 2,
        pl: 2,
        py: 1.25,
        borderLeft: 2,
        borderColor: "divider",
      }}
    >
      <FormControl fullWidth>
        <InputLabel id={labelId} sx={{ fontSize: "0.85rem" }}>
          Subunidade de referência
        </InputLabel>
        <Select
          labelId={labelId}
          label="Subunidade de referência"
          size="small"
          value={subunidadeAlvoId}
          onChange={(e) => onChange(String(e.target.value))}
          sx={{
            borderRadius: 1.5,
            fontSize: "0.85rem",
            bgcolor: "background.paper",
            "& .MuiSelect-select": {
              py: 1.1,
            },
          }}
        >
          {subunidades.map((sub) => (
            <MenuItem
              key={String(sub.id)}
              value={getOptionValue(sub)}
              sx={{ fontSize: "0.85rem", py: 1 }}
            >
              Edificação #{sub.id_edificacao}
              {sub.inscricao_subunidade ? ` · ${sub.inscricao_subunidade}` : ""}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

export function VinculoRioBrancoDialogHeader({
  icon,
  title,
  subtitle,
  onClose,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClose: () => void;
  disabled?: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 2,
        px: 3,
        pt: 3,
        pb: 2,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            color: "text.primary",
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={600} lineHeight={1.3}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        </Box>
      </Stack>
      <IconButton
        size="small"
        onClick={onClose}
        disabled={disabled}
        sx={{ color: "text.secondary", mt: -0.5, mr: -0.5 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

export function VinculoRioBrancoDialogChips({ labels }: { labels: string[] }) {
  return (
    <Box sx={{ px: 3, pb: 1 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {labels.map((label) => (
          <Chip
            key={label}
            size="small"
            label={label}
            variant="outlined"
            sx={{ borderRadius: 1.5, fontWeight: 500 }}
          />
        ))}
      </Stack>
    </Box>
  );
}

export function VinculoRioBrancoDialogLoading({ message }: { message: string }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 5, gap: 1.5 }}>
      <CircularProgress size={28} thickness={4} />
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );
}

export function VinculoRioBrancoDialogActions({
  onCancel,
  onConfirm,
  cancelLabel = "Cancelar",
  confirmLabel,
  processingLabel,
  loading,
  processing,
  confirmDisabled,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  cancelLabel?: string;
  confirmLabel: string;
  processingLabel: string;
  loading?: boolean;
  processing?: boolean;
  confirmDisabled?: boolean;
}) {
  return (
    <DialogActions
      sx={{
        px: 3,
        py: 2.5,
        gap: 1,
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      <Button
        onClick={onCancel}
        disabled={processing}
        color="inherit"
        sx={{ color: "text.secondary", fontWeight: 500 }}
      >
        {cancelLabel}
      </Button>
      <Button
        variant="contained"
        color="success"
        disabled={loading || processing || confirmDisabled}
        onClick={onConfirm}
        sx={{
          px: 2.5,
          borderRadius: 2,
          fontWeight: 600,
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        }}
      >
        {processing ? processingLabel : confirmLabel}
      </Button>
    </DialogActions>
  );
}

export function VinculoRioBrancoOpcoesList({
  opcoes,
  acaoSelecionada,
  onSelectAcao,
  subunidades,
  subunidadeAlvoId,
  onSubunidadeAlvoChange,
  subunidadeValueKey,
  subunidadeLabelId,
  ariaLabel = "Tipo de vínculo",
}: {
  opcoes: OpcaoVinculoUi[];
  acaoSelecionada: string;
  onSelectAcao: (acao: string) => void;
  subunidades?: ImobiliarioEdificacao[];
  subunidadeAlvoId?: string;
  onSubunidadeAlvoChange?: (id: string) => void;
  subunidadeValueKey?: (sub: ImobiliarioEdificacao) => string;
  subunidadeLabelId?: string;
  ariaLabel?: string;
}) {
  return (
    <Stack spacing={1.25} role="radiogroup" aria-label={ariaLabel}>
      {opcoes.map((opcao) => (
        <Box key={opcao.value}>
          <OpcaoCard
            opcao={opcao}
            selecionada={acaoSelecionada === opcao.value}
            onSelect={() => onSelectAcao(opcao.value)}
          />
          {opcao.value === "complementar_subunidade" &&
            acaoSelecionada === "complementar_subunidade" &&
            subunidades &&
            subunidades.length > 0 &&
            subunidadeAlvoId != null &&
            onSubunidadeAlvoChange && (
              <SubunidadeReferenciaSelect
                subunidades={subunidades}
                subunidadeAlvoId={subunidadeAlvoId}
                onChange={onSubunidadeAlvoChange}
                labelId={subunidadeLabelId}
                getOptionValue={subunidadeValueKey}
              />
            )}
        </Box>
      ))}
    </Stack>
  );
}

export const vinculoRioBrancoDialogPaperProps = {
  sx: {
    borderRadius: 3,
    overflow: "hidden" as const,
  },
};
